import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import Button from '../../shared/TailwindComponents/Button';
import { DashboardCalendarEvent } from '../../../store/reducer/DashboardReducer';
import {
  PiArrowRightLight,
  PiCalendarLight,
  PiMapPinLight,
  PiClockLight,
} from 'react-icons/pi';

interface UpcomingEventsProps {
  events: DashboardCalendarEvent[];
}

const eventTypeColors: Record<string, string> = {
  hearing: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  deadline: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  meeting: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  task: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  task_deadline: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  matter_deadline: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  custom: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  imported: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
};

const formatEventTime = (startTime: string | null, endTime: string | null, allDay: boolean): string => {
  if (!startTime) return '';
  if (allDay) return 'All day';

  const start = new Date(startTime);
  const now = new Date();
  const diffDays = Math.ceil((start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  const timeStr = start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  if (diffDays === 0) return `Today at ${timeStr}`;
  if (diffDays === 1) return `Tomorrow at ${timeStr}`;
  if (diffDays < 7) {
    const day = start.toLocaleDateString(undefined, { weekday: 'short' });
    return `${day} at ${timeStr}`;
  }
  return start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ` at ${timeStr}`;
};

const UpcomingEvents: React.FC<UpcomingEventsProps> = ({ events }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();

  if (!hasSectionAccess('calendar') || events.length === 0) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2.5">
          <PiCalendarLight className="w-4 h-4 text-gray-800 dark:text-gray-300" />
          <h3 className="text-sm text-gray-900 dark:text-gray-100">
            {t('Upcoming Events')}
          </h3>
        </div>
        <Button variant="outline" size="small" onClick={() => history.push('/calendar')}>
          {t('View Calendar')}
        </Button>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-700/70">
        {events.map((event) => {
          const colorClass = eventTypeColors[event.event_type] || eventTypeColors.custom;
          return (
            <div
              key={event.id}
              className="flex items-start gap-3 px-5 py-3 hover:bg-gray-50/80 dark:hover:bg-gray-750/50 cursor-pointer transition-colors"
              onClick={() => history.push('/calendar')}
            >
              <div className={`flex-shrink-0 mt-0.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${colorClass}`}>
                {event.event_type.replace(/_/g, ' ')}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                  {event.title}
                </p>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <PiClockLight className="w-3 h-3" />
                    {formatEventTime(event.start_time, event.end_time, event.all_day)}
                  </span>
                  {event.location && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 truncate">
                      <PiMapPinLight className="w-3 h-3" />
                      {event.location}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UpcomingEvents;
